import { Fragment, useCallback, useState } from 'react';
import { ChevronDown, Layers, Lock, MapPin } from 'lucide-react';
import { LogbookActionBadge, LogbookOutcomeBadge } from './LogbookBadges';
import { LogbookActor } from './LogbookActor';
import { LogbookObject } from './LogbookObject';
import { LogbookServerChip } from './LogbookServerChip';
import { LogbookEventDetail } from './LogbookEventDetail';
import { SkeletonLoader } from '../SkeletonLoader';
import { cn } from '../../utils/cn';
import { formatLocalDateTime, formatServerDateTime } from '../../utils/timestamps';
import {
  actionMeta, dimensionLabel, formatCoordinates, formatCount, hasKnownTime, hasMeaningfulQuantity,
  isEvidenceGap, relativeTime,
} from '../../utils/logbook';
import type { LogbookRun } from '../../utils/logbookView';
import type { LogbookEvent } from '../../types/logbook';
import type { LogbookServerView } from '../../hooks/useLogbookServers';

interface LogbookEventListProps {
  /** Runs, from groupEvents(). A run of one renders exactly as a single record. */
  runs: LogbookRun[];
  serversById?: Map<string, LogbookServerView>;
  loading?: boolean;
  canProtect?: boolean;
  /** Receives one record, or every record in a run when the run header is used. */
  onAddToCase?: (events: LogbookEvent[]) => void;
  onFilterActor?: (event: LogbookEvent) => void;
  onFilterAction?: (event: LogbookEvent) => void;
  onFilterNearby?: (event: LogbookEvent) => void;
  /** Event ids already copied into a case, shown with a lock. */
  heldEventIds?: Set<string>;
  /** Drops the server chip when every row is from one server. */
  hideServer?: boolean;
  /** Drops the actor when every row is the same person, as on a timeline. */
  hideActor?: boolean;
  className?: string;
}

/**
 * The evidence list.
 *
 * Each row reads as one sentence, because that is how a report arrives: someone did something to
 * something, somewhere, at a time. The badge carries the colour, the sentence carries the facts and
 * the second line carries the place. Expanding a row reveals the full record without leaving the
 * result set, so paging back and forth is not part of reading a case.
 *
 * A run of the same actor repeating the same action collapses to one summary row, because two hundred
 * identical break records are one fact, not two hundred. The summary opens into its own records, and
 * each of those still opens into the full capture, so nothing is only summarised.
 */
export const LogbookEventList = ({
  runs, serversById, loading = false, canProtect, onAddToCase, onFilterActor, onFilterAction,
  onFilterNearby, heldEventIds, hideServer = false, hideActor = false, className,
}: LogbookEventListProps) => {
  const [openRun, setOpenRun] = useState<string | null>(null);
  const [openEvent, setOpenEvent] = useState<string | null>(null);

  const toggleRun = useCallback((id: string) => {
    setOpenRun((current) => (current === id ? null : id));
  }, []);
  const toggleEvent = useCallback((id: string) => {
    setOpenEvent((current) => (current === id ? null : id));
  }, []);

  if (loading && runs.length === 0) {
    return (
      <div className={cn('divide-y divide-glass-200/10', className)}>
        {Array.from({ length: 8 }, (_, index) => (
          <div key={index} className="flex items-start gap-3 px-4 py-3">
            <SkeletonLoader className="w-7 h-7 rounded-lg shrink-0" />
            <div className="flex-1 space-y-2">
              <SkeletonLoader className="h-3.5 w-2/3" />
              <SkeletonLoader className="h-3 w-1/3" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  /** One record, as a row. Used at the top level and inside an expanded run. */
  const renderEvent = (event: LogbookEvent, options: { nested: boolean }) => {
    const meta = actionMeta(event.action);
    const server = serversById?.get(event.server_id);
    const isOpen = openEvent === event.event_id;
    const when = hasKnownTime(event) ? new Date(event.timestamp) : null;
    const held = heldEventIds?.has(event.event_id) ?? false;
    const gap = isEvidenceGap(event);

    return (
      <Fragment key={event.event_id}>
        <li className={cn(gap && 'bg-yellow-500/[0.04]')}>
          <button
            type="button"
            onClick={() => toggleEvent(event.event_id)}
            aria-expanded={isOpen}
            className={cn(
              'w-full text-left flex items-start gap-3 py-3 hover:bg-glass-200/[0.04] transition-colors',
              options.nested ? 'pl-12 pr-4' : 'px-4',
              isOpen && 'bg-glass-200/[0.06]',
            )}
          >
            <LogbookActionBadge action={event.action} iconOnly className="mt-0.5 shrink-0" />

            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap items-center gap-x-1.5 gap-y-1 text-sm min-w-0">
                {!hideActor && !options.nested && (
                  <LogbookActor actorId={event.actor_id} actorName={event.actor_name} actorKind={event.actor_kind} />
                )}
                <span className="text-glass-400">{meta.verb}</span>
                {event.object_id && (
                  <LogbookObject
                    objectId={event.object_id}
                    clusterId={server?.cluster ?? null}
                    quantity={hasMeaningfulQuantity(event) ? event.quantity : null}
                  />
                )}
                {event.outcome !== 'completed' && <LogbookOutcomeBadge outcome={event.outcome} />}
                {held && <Lock className="w-3 h-3 text-amber-300" aria-label="Preserved for a case" />}
              </div>

              <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-glass-500 min-w-0">
                <span className="font-mono tabular-nums">{dimensionLabel(event.dimension)} {formatCoordinates(event)}</span>
                {!hideServer && !options.nested && (
                  <>
                    <span aria-hidden>&middot;</span>
                    <LogbookServerChip serverId={event.server_id} server={server} />
                  </>
                )}
                <span aria-hidden>&middot;</span>
                <span
                  className="tabular-nums"
                  title={when ? `${formatLocalDateTime(when)} (${formatServerDateTime(when)})` : 'No time was recorded.'}
                >
                  {relativeTime(event.timestamp)}
                </span>
              </div>
            </div>

            <ChevronDown
              className={cn('w-4 h-4 mt-1 shrink-0 text-glass-400 transition-transform', isOpen && 'rotate-180')}
              aria-hidden
            />
          </button>
        </li>

        {isOpen && (
          <li className={cn('py-4 bg-dark-900/30', options.nested ? 'pl-12 pr-4' : 'px-4')}>
            <LogbookEventDetail
              event={event}
              server={server}
              canProtect={canProtect}
              onAddToCase={onAddToCase}
              onFilterActor={onFilterActor}
              onFilterAction={onFilterAction}
              onFilterNearby={onFilterNearby}
            />
          </li>
        )}
      </Fragment>
    );
  };

  /** A collapsed run: the same actor, action, server, outcome and dimension, close together in time. */
  const renderRun = (run: LogbookRun) => {
    const lead = run.events[0];
    const meta = actionMeta(lead.action);
    const server = serversById?.get(lead.server_id);
    const isOpen = openRun === run.id;
    const heldCount = heldEventIds ? run.events.filter((event) => heldEventIds.has(event.event_id)).length : 0;
    const spanMs = run.latestAt - run.earliestAt;
    const single = run.objectIds.length === 1 ? run.objectIds[0] : null;

    return (
      <Fragment key={run.id}>
        <li>
          <button
            type="button"
            onClick={() => toggleRun(run.id)}
            aria-expanded={isOpen}
            className={cn(
              'w-full text-left flex items-start gap-3 px-4 py-3 hover:bg-glass-200/[0.04] transition-colors',
              isOpen && 'bg-glass-200/[0.06]',
            )}
          >
            <span className="relative mt-0.5 shrink-0">
              <LogbookActionBadge action={lead.action} iconOnly />
              {/* The count badge doubles as the affordance that this row is many records. */}
              <span className="absolute -bottom-1 -right-1 px-1 min-w-[1.05rem] text-center rounded-full bg-dark-900 border border-glass-200/20 text-[10px] font-medium tabular-nums text-glass-200">
                {run.events.length > 99 ? '99+' : run.events.length}
              </span>
            </span>

            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap items-center gap-x-1.5 gap-y-1 text-sm min-w-0">
                {!hideActor && (
                  <LogbookActor actorId={lead.actor_id} actorName={lead.actor_name} actorKind={lead.actor_kind} />
                )}
                <span className="text-glass-400">{meta.verb}</span>
                {single ? (
                  <LogbookObject objectId={single} clusterId={server?.cluster ?? null} quantity={run.totalQuantity} />
                ) : run.objectIds.length > 1 ? (
                  <span className="text-white">
                    {run.totalQuantity !== null && run.totalQuantity > 0 && (
                      <span className="font-mono tabular-nums text-glass-300">{formatCount(run.totalQuantity)}&times; </span>
                    )}
                    {formatCount(run.objectIds.length)} kinds
                  </span>
                ) : null}
                <span className="badge badge-neutral gap-1">
                  <Layers className="w-3 h-3" />
                  {formatCount(run.events.length)} records
                </span>
                {lead.outcome !== 'completed' && <LogbookOutcomeBadge outcome={lead.outcome} />}
                {heldCount > 0 && (
                  <span className="inline-flex items-center gap-1 text-amber-300" title={`${heldCount} preserved for a case`}>
                    <Lock className="w-3 h-3" />
                    <span className="text-xs tabular-nums">{heldCount}</span>
                  </span>
                )}
              </div>

              <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-glass-500 min-w-0">
                <span className="font-mono tabular-nums">{dimensionLabel(lead.dimension)}</span>
                <span aria-hidden>&middot;</span>
                <span className="inline-flex items-center gap-1 tabular-nums">
                  <MapPin className="w-3 h-3" />
                  {run.positionCount === 1 ? formatCoordinates(lead) : `${formatCount(run.positionCount)} positions`}
                </span>
                {!hideServer && (
                  <>
                    <span aria-hidden>&middot;</span>
                    <LogbookServerChip serverId={lead.server_id} server={server} />
                  </>
                )}
                {spanMs > 0 && (
                  <>
                    <span aria-hidden>&middot;</span>
                    <span className="tabular-nums">over {formatSpan(spanMs)}</span>
                  </>
                )}
                <span aria-hidden>&middot;</span>
                <span
                  className="tabular-nums"
                  title={run.latestAt > 0
                    ? `Newest ${formatLocalDateTime(new Date(run.latestAt))}, oldest ${formatLocalDateTime(new Date(run.earliestAt))}`
                    : undefined}
                >
                  {relativeTime(run.latestAt)}
                </span>
              </div>
            </div>

            <ChevronDown
              className={cn('w-4 h-4 mt-1 shrink-0 text-glass-400 transition-transform', isOpen && 'rotate-180')}
              aria-hidden
            />
          </button>

          {canProtect && onAddToCase && (
            <div className="px-4 pb-3 -mt-1">
              <button
                type="button"
                className="btn-ghost btn-xs"
                onClick={() => onAddToCase(run.events)}
                title={`Copy all ${run.events.length} records into a case`}
              >
                <Lock className="w-3.5 h-3.5" />
                Add all {run.events.length} to a case
              </button>
            </div>
          )}
        </li>

        {isOpen && run.events.map((event) => renderEvent(event, { nested: true }))}
      </Fragment>
    );
  };

  return (
    <ul className={cn('divide-y divide-glass-200/10', className)}>
      {runs.map((run) => (run.grouped ? renderRun(run) : renderEvent(run.events[0], { nested: false })))}
    </ul>
  );
};

/** Compact duration for a run's span: the shape of a spree matters more than its exact seconds. */
function formatSpan(ms: number): string {
  const seconds = Math.round(ms / 1000);
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  const remainder = minutes % 60;
  return remainder === 0 ? `${hours}h` : `${hours}h ${remainder}m`;
}
